import { useEffect, useRef, useState } from "react";
import { http } from "../api";
import { AlertTriangle, MapPin, Sparkles, Mic, Square, Loader2, Trash2, Volume2, Camera } from "lucide-react";
import { Modal, PageHeader, ImportExportBar, AddButton, FormRow, EmptyState, PersonLink, PersonAutocomplete } from "../shared";
import PhotoUpload from "./PhotoUpload";

const STATUSES = ["Lodged", "Acknowledged", "Verified", "Solving", "Solved", "Closed", "Reopened"];
const PRIORITIES = ["Urgent", "High", "Medium", "Low"];
const CATEGORIES = ["Electricity", "Water Supply", "Drainage", "Sanitation", "Roads", "Ration", "Government Scheme", "Public Health", "Against Other Person", "General"];

const empty = { title: "", description: "", category: "General", priority: "Medium", ward: 7, booth: "42", assignee: "Unassigned", status: "Lodged", complainant_name: "", complainant_mobile: "", complainant_contact_id: null, other_person_name: "", other_person_mobile: "", other_person_contact_id: null, location: "", voice_id: null };

function priorityTone(p) { return { Urgent: "red", High: "amber", Medium: "blue", Low: "green" }[p] || "blue"; }
function statusTone(s) { return { Lodged: "amber", Acknowledged: "blue", Verified: "blue", Solving: "amber", Solved: "green", Closed: "green", Reopened: "red" }[s] || "blue"; }

export default function Complaints({ pageContext, clearPageContext }) {
  const [rows, setRows] = useState([]);
  const [sla, setSla] = useState({});
  const [filter, setFilter] = useState("");
  // Phase 30.4 — booth drilldown from LiveMap workload heatmap
  const [boothFilter, setBoothFilter] = useState("");
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [remark, setRemark] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiHint, setAiHint] = useState("");
  // Phase 30.1 — auto-assign preview when assignee is empty/Unassigned
  const [autoPick, setAutoPick] = useState(null);

  // Consume incoming heatmap drilldown context once, then clear it so navigating
  // away and back doesn't re-apply the stale booth.
  useEffect(() => {
    if (pageContext?.booth) {
      setBoothFilter(String(pageContext.booth));
      if (clearPageContext) clearPageContext();
    }
  }, [pageContext, clearPageContext]);

  useEffect(() => {
    if (!show) { setAutoPick(null); return undefined; }
    const emptyAssignee = !form.assignee || form.assignee === "Unassigned";
    if (!emptyAssignee || !form.booth) { setAutoPick(null); return undefined; }
    const t = setTimeout(async () => {
      try {
        const r = await http.get(`/complaints/auto-assign/preview?booth=${encodeURIComponent(form.booth)}`);
        setAutoPick(r.data);
      } catch { setAutoPick(null); }
    }, 220);
    return () => clearTimeout(t);
  }, [show, form.booth, form.assignee]);

  // Voice note state
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceHint, setVoiceHint] = useState("");
  const [recSecs, setRecSecs] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    setVoiceHint("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 500) { setVoiceHint("Recording too short"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const r = await http.post("/complaints/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
          const text = r.data.transcript || "";
          if (r.data.status === "insufficient") {
            setVoiceHint("Could not detect speech — please try again in a quieter place.");
          } else {
            setForm((f) => ({
              ...f,
              voice_id: r.data.voice_id,
              description: (f.description ? f.description + " " : "") + text,
              title: f.title || text.slice(0, 60),
              ...(r.data.suggestion ? { category: r.data.suggestion.category, priority: r.data.suggestion.priority } : {}),
            }));
            setVoiceHint(`Transcribed (${r.data.provider}) · ${text.length} chars${r.data.suggestion ? " · auto-categorised" : ""}`);
          }
        } catch (err) {
          setVoiceHint("Transcription failed. Try again.");
        } finally {
          setTranscribing(false);
        }
      };
      mediaRef.current = rec;
      rec.start();
      setRecording(true);
      setRecSecs(0);
      timerRef.current = setInterval(() => {
        setRecSecs((s) => {
          if (s + 1 >= 30) { stopRecording(); return 30; }
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      setVoiceHint("Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  useEffect(() => () => { clearInterval(timerRef.current); }, []);

  const load = async () => {
    const url = filter ? `/complaints?status=${filter}` : "/complaints";
    const [r, s] = await Promise.all([http.get(url), http.get("/complaints/sla")]);
    setRows(r.data);
    const slaMap = {};
    s.data.complaints.forEach((c) => { slaMap[c.complaint_id] = c; });
    setSla(slaMap);
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [filter]);

  // Voice notes archive (retention control)
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveOnlyUnlinked, setArchiveOnlyUnlinked] = useState(false);
  const [purging, setPurging] = useState(false);

  const loadVoiceNotes = async (onlyUnlinked = archiveOnlyUnlinked) => {
    try {
      const r = await http.get(`/voice-notes${onlyUnlinked ? "?only_unlinked=true" : ""}`);
      setVoiceNotes(r.data);
    } catch { /* noop */ }
  };
  const openArchive = async () => { setShowArchive(true); await loadVoiceNotes(false); setArchiveOnlyUnlinked(false); };
  const toggleUnlinked = async () => { const next = !archiveOnlyUnlinked; setArchiveOnlyUnlinked(next); await loadVoiceNotes(next); };
  const deleteNote = async (vid) => {
    if (!window.confirm("Delete this voice recording? This cannot be undone.")) return;
    await http.delete(`/voice-notes/${vid}`);
    await loadVoiceNotes(archiveOnlyUnlinked);
  };
  const purgeOld = async () => {
    if (!window.confirm("Purge unlinked voice recordings older than 30 days?")) return;
    setPurging(true);
    try {
      const r = await http.post("/voice-notes/purge?older_than_days=30&only_unlinked=true");
      alert(`Purged ${r.data.deleted} recording(s).`);
      await loadVoiceNotes(archiveOnlyUnlinked);
    } finally { setPurging(false); }
  };

  const save = async () => {
    // Phase 18 — Booth, Ward and Complainant mobile are mandatory
    const missing = [];
    if (!form.booth || !String(form.booth).trim()) missing.push("Booth");
    if (form.ward === "" || form.ward === null || form.ward === undefined) missing.push("Ward");
    if (!form.complainant_mobile || !String(form.complainant_mobile).trim()) missing.push("Complainant mobile");
    // Phase 29 — Against Other Person requires the "other person" name
    if (form.category === "Against Other Person" && (!form.other_person_name || !String(form.other_person_name).trim())) {
      missing.push("Other person name");
    }
    if (missing.length) {
      window.alert(`Please fill required field(s): ${missing.join(", ")}`);
      return;
    }
    setSaving(true);
    try { await http.post("/complaints", { ...form, ward: Number(form.ward) }); setShow(false); setForm(empty); setAiHint(""); load(); }
    catch (e) { window.alert(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const aiSuggest = async () => {
    if (!form.title.trim()) return;
    setAiBusy(true); setAiHint("");
    try {
      const r = await http.post("/complaints/categorise", { title: form.title, description: form.description });
      setForm((f) => ({ ...f, category: r.data.category, priority: r.data.priority }));
      setAiHint(`${r.data.provider === "openai/gpt-4o-mini" ? "AI" : "Rules"} · ${Math.round((r.data.confidence || 0) * 100)}% confidence · ${r.data.reason || ""}`);
    } catch (e) { setAiHint("AI suggestion failed"); }
    finally { setAiBusy(false); }
  };

  const updateStatus = async (c, next) => {
    await http.patch(`/complaints/${c.complaint_id}`, { status: next });
    load();
    if (detail) setDetail({ ...detail, status: next });
  };

  const addRemark = async () => {
    if (!remark.trim()) return;
    await http.patch(`/complaints/${detail.complaint_id}`, { remark });
    const r = await http.get("/complaints");
    const upd = r.data.find((x) => x.complaint_id === detail.complaint_id);
    setDetail(upd); setRows(r.data); setRemark("");
  };

  const setPhoto = async (kind, fileId) => {
    if (!detail) return;
    const payload = kind === "before" ? { before_photo_id: fileId || "" } : { after_photo_id: fileId || "" };
    await http.patch(`/complaints/${detail.complaint_id}`, payload);
    const r = await http.get("/complaints");
    const upd = r.data.find((x) => x.complaint_id === detail.complaint_id);
    setDetail(upd); setRows(r.data);
  };
  const canShowPhotos = detail && (detail.status === "Solving" || detail.status === "Verified" || detail.status === "Solved" || detail.status === "Closed");

  return (
    <>
      <PageHeader eyebrow="GRIEVANCE QUEUE" title="Complaints" subtitle={`${rows.length} records`}
        actions={<>
          <button className="outline" data-testid="voice-archive-button" onClick={openArchive}><Volume2 size={14} /> Voice archive</button>
          <ImportExportBar entity="complaints" onImported={load} />
          <AddButton onClick={() => setShow(true)} label="Lodge complaint" testid="complaints-add-button" />
        </>} />

      <div className="chip-row">
        <button className={`chip-filter ${!filter ? "active" : ""}`} data-testid="filter-all" onClick={() => setFilter("")}>All</button>
        {STATUSES.map((s) => (
          <button key={s} className={`chip-filter ${filter === s ? "active" : ""}`} data-testid={`filter-${s.toLowerCase()}`} onClick={() => setFilter(s)}>{s}</button>
        ))}
        {boothFilter && (
          <button
            className="chip-filter active"
            data-testid="booth-filter-chip"
            title="Clear booth filter"
            onClick={() => setBoothFilter("")}
            style={{ background: "#fde68a", color: "#92400e", borderColor: "#fbbf24" }}
          >
            <MapPin size={12} /> Booth {boothFilter} ✕
          </button>
        )}
      </div>

      <section className="panel">
        {(() => {
          const visible = boothFilter ? rows.filter((c) => String(c.booth) === String(boothFilter)) : rows;
          if (visible.length === 0) {
            return (
              <EmptyState
                icon={AlertTriangle}
                title={boothFilter ? `No open complaints in Booth ${boothFilter}` : "No complaints"}
                hint={boothFilter ? "Nothing to triage here right now — clear the booth filter to see the full queue." : "Lodge a new complaint to begin."}
              />
            );
          }
          return (
          <div className="complaint-list">
            {visible.map((c, i) => {
              const s = sla[c.complaint_id];
              const overdue = s?.breached;
              const critical = s?.critical;
              return (
                <button key={c.complaint_id} className="complaint-row" data-testid={`complaint-row-${i}`} onClick={() => setDetail(c)}>
                  <div className="c-left">
                    <div className="c-head">
                      <span className={`chip ${priorityTone(c.priority)}`}>{c.priority}</span>
                      <b>{c.complaint_id}</b>
                      <span className="c-title">{c.title}</span>
                    </div>
                    <small><MapPin size={12} /> Booth {c.booth} · {c.category} · {c.assignee}</small>
                  </div>
                  {s && (
                    <span className={`sla-badge ${overdue ? "sla-breached" : critical ? "sla-critical" : "sla-ok"}`} data-testid={`sla-badge-${i}`}>
                      {overdue ? `⚠ Overdue ${Math.abs(s.hours_left).toFixed(1)}h`
                        : critical ? `⏱ ${s.hours_left.toFixed(1)}h left`
                        : `${s.hours_left.toFixed(1)}h SLA`}
                    </span>
                  )}
                  <span className={`chip ${statusTone(c.status)}`}>{c.status}</span>
                </button>
              );
            })}
          </div>
          );
        })()}
      </section>

      {show && (
        <Modal title="Lodge complaint" onClose={() => setShow(false)} testid="complaints-modal">
          <FormRow label="Title"><input data-testid="form-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
          <FormRow label="Description"><input data-testid="form-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <div className="ai-suggest-row">
            <button type="button" className="outline" data-testid="ai-suggest-button" disabled={aiBusy || !form.title.trim()} onClick={aiSuggest}>
              <Sparkles size={14} /> {aiBusy ? "Analysing…" : "AI suggest category + priority"}
            </button>
            {aiHint && <small className="ai-hint" data-testid="ai-hint">{aiHint}</small>}
          </div>
          <div className="ai-suggest-row" data-testid="voice-note-row">
            {!recording ? (
              <button type="button" className="outline" data-testid="voice-record-button" disabled={transcribing} onClick={startRecording}>
                {transcribing ? <><Loader2 size={14} className="spin" /> Transcribing…</> : <><Mic size={14} /> Record voice note (30s)</>}
              </button>
            ) : (
              <button type="button" className="outline" style={{ borderColor: "#b04a3f", color: "#b04a3f" }} data-testid="voice-stop-button" onClick={stopRecording}>
                <Square size={12} fill="currentColor" /> Stop · {recSecs}s / 30s
              </button>
            )}
            {voiceHint && <small className="ai-hint" data-testid="voice-hint">{voiceHint}</small>}
          </div>
          <div className="form-grid">
            <FormRow label="Category"><select data-testid="form-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></FormRow>
            <FormRow label="Priority"><select data-testid="form-priority-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>{PRIORITIES.map((p) => <option key={p}>{p}</option>)}</select></FormRow>
            <FormRow label="Booth *"><input data-testid="form-booth-input" required value={form.booth} onChange={(e) => setForm({ ...form, booth: e.target.value })} placeholder="Required" /></FormRow>
            <FormRow label="Ward *"><input data-testid="form-ward-input" required type="number" value={form.ward} onChange={(e) => setForm({ ...form, ward: e.target.value })} placeholder="Required" /></FormRow>
            <FormRow label="Assignee">
              <PersonAutocomplete
                testid="form-assignee-autocomplete"
                value={form.assignee === "Unassigned" ? "" : form.assignee}
                onChange={(v) => setForm((f) => ({ ...f, assignee: v || "Unassigned", assignee_contact_id: null }))}
                onSelect={(v) => setForm((f) => ({
                  ...f,
                  assignee: v.name || "Unassigned",
                  assignee_contact_id: v.contact_id || null,
                  booth: f.booth || v.booth || "",
                }))}
                source="/volunteers/autocomplete"
                placeholder="Search active volunteers…"
              />
              {autoPick && (
                <div data-testid="auto-assign-hint" style={{ marginTop: 6, fontSize: 11, color: "#065f46", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {autoPick.suggested ? (
                    <>
                      <span>Auto-assign will pick:</span>
                      <b>{autoPick.suggested.name}</b>
                      <span className="chip green" style={{ fontSize: 10 }}>{autoPick.suggested.open_complaints} open · {autoPick.suggested.points} pts</span>
                      <button type="button" className="link" data-testid="auto-assign-accept"
                        onClick={() => setForm((f) => ({ ...f, assignee: autoPick.suggested.name, assignee_contact_id: autoPick.suggested.contact_id || null }))}
                        style={{ color: "#0f766e", textDecoration: "underline", fontWeight: 700, background: "none", padding: 0 }}>
                        Accept
                      </button>
                    </>
                  ) : (
                    <span style={{ color: "#92400e" }}>{autoPick.reason || "No active volunteer in this booth — assign manually."}</span>
                  )}
                </div>
              )}
            </FormRow>
            <FormRow label="Complainant name">
              <PersonAutocomplete
                testid="form-comp-name-autocomplete"
                value={form.complainant_name}
                onChange={(v) => setForm((f) => ({ ...f, complainant_name: v, complainant_contact_id: null }))}
                onSelect={(c) => setForm((f) => ({
                  ...f,
                  complainant_name: c.name || "",
                  complainant_contact_id: c.contact_id,
                  complainant_mobile: f.complainant_mobile || c.mobile || "",
                  booth: f.booth || c.booth || "",
                }))}
                placeholder="Start typing a name…"
              />
            </FormRow>
            <FormRow label="Complainant mobile *"><input data-testid="form-comp-mobile-input" required value={form.complainant_mobile} onChange={(e) => setForm({ ...form, complainant_mobile: e.target.value })} placeholder="Required" /></FormRow>
          </div>
          {form.category === "Against Other Person" && (
            <section className="panel" data-testid="other-person-section" style={{ marginTop: 12, padding: "12px 14px", background: "#fef3c7", borderRadius: 10 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 12, letterSpacing: ".4px", color: "#92400e" }}>ENTER OTHER PERSON DETAILS *</h4>
              <div className="form-grid">
                <FormRow label="Other person name *">
                  <PersonAutocomplete
                    testid="form-other-name-autocomplete"
                    value={form.other_person_name}
                    onChange={(v) => setForm((f) => ({ ...f, other_person_name: v, other_person_contact_id: null }))}
                    onSelect={(c) => setForm((f) => ({
                      ...f,
                      other_person_name: c.name || "",
                      other_person_contact_id: c.contact_id,
                      other_person_mobile: f.other_person_mobile || c.mobile || "",
                    }))}
                    placeholder="Start typing a name…"
                  />
                </FormRow>
                <FormRow label="Other person mobile">
                  <input data-testid="form-other-mobile-input" value={form.other_person_mobile} onChange={(e) => setForm({ ...form, other_person_mobile: e.target.value })} placeholder="Optional" />
                </FormRow>
              </div>
              <small className="hint" style={{ marginTop: 4, display: "block" }}>
                If the person isn&apos;t in the CRM we&apos;ll auto-create a lightweight profile so you can back-link them.
              </small>
            </section>
          )}
          <FormRow label="Location"><input data-testid="form-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FormRow>
          <div className="form-actions">
            <button className="outline" data-testid="form-cancel-button" onClick={() => setShow(false)}>Cancel</button>
            <button className="primary" data-testid="form-save-button" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : "Lodge complaint"}</button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`${detail.complaint_id} · ${detail.title}`} onClose={() => setDetail(null)} testid="complaint-detail-modal">
          <p className="hint">{detail.description}</p>
          <div className="hh-meta">
            <span className={`chip ${priorityTone(detail.priority)}`}>{detail.priority}</span>
            <span className={`chip ${statusTone(detail.status)}`}>{detail.status}</span>
            <span>Ward {detail.ward} · Booth {detail.booth}</span>
            <span>{detail.category}</span>
          </div>
          {(detail.complainant_name || detail.complainant_mobile) && (
            <p><b>Complainant:</b> <PersonLink contactId={detail.complainant_contact_id} name={detail.complainant_name} testid={`detail-complainant-${detail.complaint_id}`}>{detail.complainant_name}</PersonLink> {detail.complainant_mobile}</p>
          )}
          {(detail.other_person_name || detail.other_person_mobile) && (
            <p data-testid="detail-other-person"><b>Against:</b> <PersonLink contactId={detail.other_person_contact_id} name={detail.other_person_name} testid={`detail-other-person-${detail.complaint_id}`}>{detail.other_person_name}</PersonLink> {detail.other_person_mobile}</p>
          )}
          <p><b>Assignee:</b> <PersonLink contactId={detail.assignee_contact_id} name={detail.assignee} testid={`detail-assignee-${detail.complaint_id}`}>{detail.assignee}</PersonLink></p>

          <h4 style={{ marginTop: 15, fontSize: 12 }}>
            <Camera size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
            BEFORE / AFTER PHOTOS <small style={{ fontWeight: 400, color: "var(--muted)" }}>(shown on the public Ward Wall when Solved)</small>
          </h4>
          {canShowPhotos ? (
            <div style={{ display: "flex", gap: 20, marginTop: 8, flexWrap: "wrap" }} data-testid="complaint-photo-pair">
              <div style={{ flex: 1, minWidth: 180 }}>
                <PhotoUpload category="complaint" value={detail.before_photo_id || ""} onChange={(v) => setPhoto("before", v)} shape="banner" label="Before" testid="complaint-photo-before" />
              </div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <PhotoUpload category="complaint" value={detail.after_photo_id || ""} onChange={(v) => setPhoto("after", v)} shape="banner" label="After" testid="complaint-photo-after" />
              </div>
            </div>
          ) : (
            <p className="hint" style={{ margin: "6px 0 0" }}>Move the complaint to Verified/Solving to attach a before/after photo pair.</p>
          )}

          <h4 style={{ marginTop: 15, fontSize: 12 }}>MOVE STATUS</h4>
          <div className="chip-row">
            {STATUSES.map((s) => (
              <button key={s} className={`chip-filter ${detail.status === s ? "active" : ""}`}
                data-testid={`transition-${s.toLowerCase()}`} onClick={() => updateStatus(detail, s)}>{s}</button>
            ))}
          </div>

          <h4 style={{ marginTop: 15, fontSize: 12 }}>REMARKS ({(detail.remarks || []).length})</h4>
          <div className="remarks-list">
            {(detail.remarks || []).map((r, i) => (
              <div key={i} className="remark" data-testid={`remark-${i}`}>
                <b>{r.by}</b> <small>· {new Date(r.at).toLocaleString()}</small>
                <p>{r.text}</p>
              </div>
            ))}
          </div>
          <div className="remark-add">
            <input data-testid="remark-input" placeholder="Add remark…" value={remark} onChange={(e) => setRemark(e.target.value)} />
            <button className="primary" data-testid="add-remark-button" onClick={addRemark}>Add</button>
          </div>
        </Modal>
      )}

      {showArchive && (
        <Modal title="Voice recordings archive" onClose={() => setShowArchive(false)} testid="voice-archive-modal">
          <div className="chip-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <button className={`chip-filter ${archiveOnlyUnlinked ? "active" : ""}`} data-testid="archive-toggle-unlinked" onClick={toggleUnlinked}>
              {archiveOnlyUnlinked ? "Showing unlinked only" : "Show unlinked only"}
            </button>
            <button className="outline" data-testid="archive-purge-button" disabled={purging} onClick={purgeOld}>
              <Trash2 size={14} /> {purging ? "Purging…" : "Purge unlinked > 30 days"}
            </button>
          </div>
          <p className="hint" style={{ marginTop: 8 }}>
            {voiceNotes.length} recording(s). Notes linked to a complaint are safe until you delete them explicitly.
          </p>
          {voiceNotes.length === 0 ? (
            <EmptyState icon={Volume2} title="No recordings" hint="Voice notes appear here after residents record complaints." />
          ) : (
            <div className="complaint-list" data-testid="voice-archive-list" style={{ maxHeight: 380, overflowY: "auto" }}>
              {voiceNotes.map((v, i) => (
                <div key={v.voice_id} className="complaint-row" data-testid={`voice-note-row-${i}`} style={{ cursor: "default" }}>
                  <div className="c-left">
                    <div className="c-head">
                      <span className={`chip ${v.complaint_id ? "green" : "amber"}`}>{v.complaint_id ? `Linked · ${v.complaint_id}` : "Unlinked"}</span>
                      <b>{v.provider}</b>
                      <span className="c-title">{(v.transcript || "").slice(0, 80) || "(no transcript)"}</span>
                    </div>
                    <small>{new Date(v.created_at).toLocaleString()} · {v.uploaded_by} · {Math.round((v.size_bytes || 0) / 1024)} KB</small>
                  </div>
                  <button className="outline" data-testid={`voice-note-delete-${i}`} onClick={() => deleteNote(v.voice_id)} style={{ color: "#b04a3f", borderColor: "#b04a3f" }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
